import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

// WhatsApp phone number in international format (no + or spaces)
const WHATSAPP_NUMBER = '31570238004';

const WhatsAppButton = () => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const { ui } = useLang();
  const w = ui.wa;
  const msg = encodeURIComponent(w.msg);

  useEffect(() => {
    // Fade in after mount
    const t = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      {/* Tooltip / popover */}
      {open && (
        <div className="fixed bottom-28 right-6 md:right-8 z-[100] w-72 rounded-2xl bg-[#111114] border border-white/10 shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-2">
          {/* Header */}
          <div className="bg-[#25D366] px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="#fff">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M20.52 3.449C12.831-3.984.106 1.407.101 11.893c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652c7.191 3.895 15.855-1.264 15.86-10.446 0-3.176-1.24-6.166-3.492-8.42zM12 21.785c-1.868 0-3.696-.502-5.29-1.448l-.379-.226-3.75.978 1.002-3.646-.246-.391A9.888 9.888 0 012.001 11.893c-.005-6.994 8.478-10.481 13.407-5.554 5.14 4.926 1.598 13.415-5.406 13.446z"/>
              </svg>
            </div>
            <div>
              <div className="text-white font-medium text-sm">Shiny-Cleaning</div>
              <div className="text-white/85 text-xs">{w.subtitle}</div>
            </div>
          </div>
          <div className="p-5">
            <p className="text-white/80 text-sm mb-4">
              {w.body}
            </p>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-full gap-2 px-5 py-3 rounded-md bg-[#25D366] hover:bg-[#20bd5a] text-white text-sm font-medium transition-colors"
            >
              {w.start}
            </a>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setOpen(v => !v)}
        aria-label="WhatsApp"
        className={`fixed bottom-6 right-6 md:bottom-8 md:right-8 z-[100] w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-xl flex items-center justify-center transition-all duration-500 ${
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
        }`}
      >
        {open ? (
          <X className="w-6 h-6" />
        ) : (
          <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
            <path d="M20.52 3.449C12.831-3.984.106 1.407.101 11.893c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652c7.191 3.895 15.855-1.264 15.86-10.446 0-3.176-1.24-6.166-3.492-8.42zM12 21.785c-1.868 0-3.696-.502-5.29-1.448l-.379-.226-3.75.978 1.002-3.646-.246-.391A9.888 9.888 0 012.001 11.893c-.005-6.994 8.478-10.481 13.407-5.554 5.14 4.926 1.598 13.415-5.406 13.446z"/>
          </svg>
        )}
        {/* Pulse ring when closed */}
        {!open && (
          <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping" style={{ animationDuration: '2.5s' }} />
        )}
      </button>
    </>
  );
};

export default WhatsAppButton;
